import {useState, useMemo, useEffect, forwardRef, useImperativeHandle} from 'react'

import {selectTheme} from '../../store'

import {ChevronLeftIcon, ChevronRightIcon, DoubleChevronIcon, CalendarGreyIcon} from '../../asset/image'

import {IProject} from '../../shared/db'
import {Calendar} from 'react-calendar'
import styled from '@emotion/styled'
import {RequestAdherencePayload, AttemptRequestParticipantStatus} from '../pages/request_project_data_hook'

export interface AdherenceTableDateControllerProps {
  project?: IProject
  initialDatePickerIndex: number
  dateList: number[]
  garminDirectBbiEnabled?: boolean
  garminConnectEnabled?: boolean
  datePickerIndexUpdated: (datePickerIndex: number) => void
}

export type DateControllerRefType = {
  updateViewRangeIndexes: (startIndex: number, endIndex: number) => void
}

export const DateController = forwardRef((prop: AdherenceTableDateControllerProps, ref) => {
  const {
    project,
    initialDatePickerIndex,
    dateList,
    garminDirectBbiEnabled,
    garminConnectEnabled,
    datePickerIndexUpdated,
  } = prop
  const {color, fontWeight, fontSize} = selectTheme()
  const [renderDatePicker, setRenderDatePicker] = useState(false)
  // Index pointer used to track the date in date picker
  const [datePickerIndex, setDatePickerIndex] = useState<number>(initialDatePickerIndex)

  const safeSetDatePickerIndex = (index: number) => {
    if (index < 0) setDatePickerIndex(0)
    else if (index > dateList.length - 1) setDatePickerIndex(dateList.length - 1)
    else setDatePickerIndex(index)
  }

  // Index pointer used to track the load data range
  const [loadDateIndex, setLoadDataIndex] = useState<[number, number]>([0, 0])
  useImperativeHandle(ref, () => ({
    updateViewRangeIndexes: (startIndex: number, endIndex: number) => {
      setLoadDataIndex([startIndex, endIndex])
      safeSetDatePickerIndex(endIndex)
    },
  }))

  // initial loadingIndex
  useEffect(() => {
    if (dateList.length) {
      const endIndex = dateList.length - 1
      const startindex = endIndex > 30 ? endIndex - 30 : 0
      setLoadDataIndex([startindex, endIndex])
    }
  }, [dateList])

  const loadDataDateRange = useMemo(() => {
    if (loadDateIndex[0] == loadDateIndex[1]) {
      return [dateList[loadDateIndex[0]], dateList[loadDateIndex[1]]]
    }

    const maxIndex = dateList.length - 1
    let dateEndIndex = loadDateIndex[1] + 3 // extra 3 days after
    if (dateEndIndex > maxIndex) {
      dateEndIndex = maxIndex
    }
    return [dateList[loadDateIndex[0]], dateList[dateEndIndex]]
  }, [loadDateIndex])

  const requestGarminDataDigestRange = useMemo(() => {
    if (loadDataDateRange) return {yymmddIndexStart: loadDataDateRange[0], yymmddIndexEnd: loadDataDateRange[1]}
  }, [loadDataDateRange])

  const requestAdherencePayload = useMemo<RequestAdherencePayload | undefined>(() => {
    if (loadDataDateRange) {
      const yymmIndexOfCoveredMonth = [...new Set(loadDataDateRange.map((d) => ~~(d / 100)))]
      return {yymmIndexToFetch: yymmIndexOfCoveredMonth}
    }
    return
  }, [loadDataDateRange])

  AttemptRequestParticipantStatus({
    requestAdherence: requestAdherencePayload,
    requestGarminDirectBbiDigest: garminDirectBbiEnabled ? requestGarminDataDigestRange : undefined,
    requestGarminConnectWearTime: garminConnectEnabled ? requestGarminDataDigestRange : undefined,
  })

  useEffect(() => {
    datePickerIndexUpdated(datePickerIndex)
  }, [datePickerIndex])

  const dateFromDatePickerIndex = useMemo(() => {
    const yymmdd = dateList[datePickerIndex]
    const y = 2000 + Math.floor(yymmdd / 10000)
    const m = Math.floor((yymmdd % 10000) / 100) - 1
    const d = yymmdd % 100
    const today = new Date(y, m, d, 0, 0, 0, 0)
    return today
  }, [datePickerIndex])

  function isDateInRange(dateToCheck: Date | string): boolean {
    const check = new Date(dateToCheck)

    check.setHours(0, 0, 0, 0)
    const start = new Date(project?.createdAt ?? '')
    const end = new Date()
    start.setHours(0, 0, 0, 0)
    end.setHours(0, 0, 0, 0)

    const startTime = start.getTime()
    const endTime = end.getTime()
    const checkTime = check.getTime()

    return checkTime >= startTime && checkTime <= endTime
  }

  const shiftDates = (offset: number) => {
    const maxIndex = dateList.length - 1

    let newStart = loadDateIndex[0] + offset
    if (newStart < 0) {
      newStart = 0
    } else if (newStart > maxIndex) {
      newStart = maxIndex
    }

    let newEnd = loadDateIndex[1] + offset
    if (newEnd < 0) {
      newEnd = 0
    } else if (newEnd > maxIndex) {
      newEnd = maxIndex
    }

    setLoadDataIndex([newStart, newEnd])
    safeSetDatePickerIndex(newEnd)
  }

  const handleRollbackOneDay = () => {
    shiftDates(-1)
  }

  const handleRollbackOneWeek = () => {
    shiftDates(-7)
  }

  const forwardOneDay = () => {
    shiftDates(1)
  }

  const forwardOneWeek = () => {
    shiftDates(7)
  }

  const CalendarContainer = styled.div`
    width: 100%;
    height: 100%;

    button {
      border: none;
      background-color: transparent;
    }

    button:disabled {
      cursor: not-allowed;
    }

    button:disabled abbr {
      color: ${color.textIcon.disabled};
    }

    .react-calendar {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .react-calendar__navigation {
      width: 100%;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .react-calendar__navigation__prev2-button {
      display: none;
    }

    .react-calendar__navigation__prev-button {
      background-image: url(${ChevronLeftIcon});
    }

    .react-calendar__navigation__next2-button {
      display: none;
    }

    .react-calendar__navigation__next-button {
      background-image: url(${ChevronRightIcon});
    }

    .react-calendar__navigation__arrow {
      width: 24px;
      height: 24px;
      border: 1px solid ${color.border.light};
      border-radius: 5px;
      color: transparent;
      background-size: 24px;
      background-repeat: no-repeat;
      background-position: center;
      cursor: pointer;
      &:hover {
        background-color: ${color.surface.hover.hoverWhiteBg};
      }
    }

    .react-calendar__navigation__label__labelText {
      font-weight: ${fontWeight.thick};
    }

    .react-calendar__viewContainer {
      flex: 1;
      display: flex;
    }

    .react-calendar__month-view {
      flex: 1;
    }

    .react-calendar__month-view__weekdays {
      margin-bottom: 8px;
    }

    .react-calendar__tile {
      height: 32px;
      border-radius: 5px;
      cursor: pointer;
      &:hover {
        background-color: ${color.surface.hover.hoverWhiteBg};
      }
    }

    .calendar__century-view__decades__decade {
      font-size: ${fontSize.h9};
    }

    .react-calendar__navigation__label {
      width: 120px;
      max-width: 140px;
      height: 24px;
      border-radius: 5px;
      cursor: pointer;
      &:hover {
        background-color: ${color.surface.hover.hoverWhiteBg};
      }
    }

    .react-calendar__month-view__weekdays__weekday {
      display: flex;
      justify-content: center;
    }

    .react-calendar__tile {
      border-radius: 5px;
      cursor: pointer;
      &:hover {
        background-color: ${color.surface.hover.hoverWhiteBg};
      }
    }

    .react-calendar__tile--active {
      background-color: ${color.black};
      &:hover {
        background-color: ${color.black};
      }
    }

    .react-calendar__tile--active abbr {
      color: ${color.white};
    }
  `
  const rollBackByMonth = (n: number, fromDate: Date = new Date()) => {
    let totalDays = 0
    const current = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1)

    for (let i = 1; i <= n; i++) {
      const targetMonth = new Date(current.getFullYear(), current.getMonth() - i, 1)
      const year = targetMonth.getFullYear()
      const month = targetMonth.getMonth()

      const daysInMonth = new Date(year, month + 1, 0).getDate()
      totalDays += daysInMonth
    }
    const index = dateList.length - 1 - totalDays
    return safeSetDatePickerIndex(index)
  }
  const DatePickerButton = (props: {text: string; onClick: () => void; last?: boolean}) => {
    const {text, onClick, last} = props
    return (
      <button
        onClick={onClick}
        aria-label={'Select a date'}
        css={{
          width: '100%',
          height: 48,
          padding: '16px 24px',
          cursor: 'pointer',
          border: 'none',
          borderBottom: last ? 'none' : `1px solid ${color.border.light}`,
          backgroundColor: color.surface.white,
          textAlign: 'left',
          fontSize: fontSize.h7,
          whiteSpace: 'nowrap',
        }}>
        {text}
      </button>
    )
  }

  const handleDatePickerChange = (inputDate: Date) => {
    const today = new Date()
    const date = new Date(inputDate)

    today.setHours(0, 0, 0, 0)
    date.setHours(0, 0, 0, 0)

    const diffTime = today.getTime() - date.getTime()
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24))
    const index = dateList.length - diffDays - 1
    return safeSetDatePickerIndex(index)
  }

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const parseYYMMDDIndexToPrettyDate = (yymmddIndex: number) => {
    const month = Math.trunc((yymmddIndex % 10000) / 100)
    const date = yymmddIndex % 100
    return `${months[month - 1]} ${date}`
  }

  return (
    <div
      css={{
        position: 'relative',
        height: '40px',
        width: '280px',
        padding: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: color.white,
        border: `1px solid ${color.grey_160}`,
        borderRadius: '5px',
        gap: '8px',
      }}>
      {renderDatePicker && (
        <div
          css={{
            position: 'absolute',
            right: 0,
            top: 45,
            width: '447px',
            height: '288px',
            borderRadius: 16,
            border: `1px solid ${color.border.light}`,
            backgroundColor: color.surface.white,
            boxShadow: `0 4px 14px 0 rgba(115, 115, 115, 0.25)`,
            display: 'flex',
          }}>
          <div
            css={{
              height: '100%',
              width: '196px',
              borderRight: `1px solid ${color.border.light}`,
            }}>
            <DatePickerButton
              {...{
                text: 'Today',
                onClick: () => safeSetDatePickerIndex(dateList.length - 1),
              }}
            />
            <DatePickerButton
              {...{
                text: '7 days ago (from today)',
                onClick: () => safeSetDatePickerIndex(dateList.length - 8),
              }}
            />
            <DatePickerButton
              {...{
                text: '14 days ago (from today)',
                onClick: () => safeSetDatePickerIndex(dateList.length - 15),
              }}
            />
            <DatePickerButton
              {...{
                text: '21 days ago (from today)',
                onClick: () => safeSetDatePickerIndex(dateList.length - 22),
              }}
            />
            <DatePickerButton
              {...{
                text: '1 month ago (from today)',
                onClick: () => rollBackByMonth(1),
              }}
            />
            <DatePickerButton
              {...{
                text: '2 month ago (from today)',
                onClick: () => rollBackByMonth(2),
              }}
            />
          </div>
          <div
            css={{
              flex: 1,
              padding: 10,
            }}>
            <CalendarContainer>
              <Calendar
                onChange={(e) => handleDatePickerChange(e as Date)}
                maxDate={new Date()}
                minDate={new Date(project?.createdAt ?? '')}
                tileDisabled={({date}) => !isDateInRange(date)}
                value={dateFromDatePickerIndex}
                locale='en-US'
              />
            </CalendarContainer>
          </div>
        </div>
      )}
      <button
        onClick={handleRollbackOneWeek}
        aria-label={'Arrow to select previous week'}
        css={{
          cursor: 'pointer',
          width: '24px',
          minWidth: '24px',
          height: '24px',
          minHeight: '24px',
          border: 'none',
          rotate: '180deg',
          backgroundImage: `url(${DoubleChevronIcon})`,
          backgroundSize: '24px',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundColor: 'transparent',
          borderRadius: '5px',
          ':hover': {
            backgroundColor: color.surface.hover.hoverWhiteBg,
          },
        }}
      />
      <button
        onClick={handleRollbackOneDay}
        aria-label={'Arrow to select previous day'}
        css={{
          cursor: 'pointer',
          width: '24px',
          minWidth: '24px',
          height: '24px',
          minHeight: '24px',
          border: 'none',
          backgroundImage: `url(${ChevronLeftIcon})`,
          backgroundSize: '24px',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundColor: 'transparent',
          borderRadius: '5px',
          ':hover': {
            backgroundColor: color.surface.hover.hoverWhiteBg,
          },
        }}
      />
      <div
        css={{
          width: 0,
          height: 16,
          borderRight: `1px solid ${color.border.default}`,
        }}
      />
      <button
        onClick={() => setRenderDatePicker(!renderDatePicker)}
        css={{
          height: 24,
          width: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          borderRadius: '5px',
          ':hover': {
            backgroundColor: color.surface.hover.hoverWhiteBg,
          },
        }}>
        <img src={CalendarGreyIcon} width={20} height={20} alt={'Calendar icon'} />
        <p>{parseYYMMDDIndexToPrettyDate(dateList[datePickerIndex])}</p>
      </button>
      <div
        css={{
          width: 0,
          height: 16,
          borderRight: `1px solid ${color.border.default}`,
        }}
      />
      <button
        onClick={forwardOneDay}
        aria-label={'Arrow to select next day'}
        css={{
          cursor: 'pointer',
          width: '24px',
          minWidth: '24px',
          height: '24px',
          minHeight: '24px',
          border: 'none',
          backgroundImage: `url(${ChevronRightIcon})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundColor: 'transparent',
          borderRadius: '5px',
          ':hover': {
            backgroundColor: color.surface.hover.hoverWhiteBg,
          },
        }}
      />
      <button
        onClick={forwardOneWeek}
        aria-label={'Arrow to select next week'}
        css={{
          cursor: 'pointer',
          width: '24px',
          minWidth: '24px',
          height: '24px',
          minHeight: '24px',
          border: 'none',
          backgroundImage: `url(${DoubleChevronIcon})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundColor: 'transparent',
          borderRadius: '5px',
          ':hover': {
            backgroundColor: color.surface.hover.hoverWhiteBg,
          },
        }}
      />
    </div>
  )
})
